import get from 'lodash/get';
import * as clientStorage from './client-storage';
import { StorageKey } from './client-storage';
import { getQueryParameter } from './query-parameters';

const GQL_URL = `${process.env.REACT_APP_API_HOST}/graphql`;

export const GQL_ERROR = {
  UNAUTHORIZED: 'Unauthorized',
  TIMEOUT: 'Timeout',
};

const TIMEOUT_RESPONSE_HEADER = 'x-azure-externalerror';

function getQueryAuthToken ():string {
  const jwtToken = getQueryParameter('access_token');
  if (jwtToken) {
    return decodeURIComponent(jwtToken);
  }

  const legacyToken = getQueryParameter('token');
  if (legacyToken) {
    return decodeURIComponent(legacyToken);
  }

  return '';
}

function getAuthorizationHeader ():string {
  const queryAuthToken = getQueryAuthToken();
  if (queryAuthToken) {
    return queryAuthToken.startsWith('Bearer ')
      ? queryAuthToken
      : `Bearer ${queryAuthToken}`;
  }

  const storedToken = clientStorage.get(StorageKey.TOKEN);
  return storedToken ? `Bearer ${storedToken}` : '';
}

/**
 * Serializes data payload passed as an argument to a GraphQL mutation
 * @param object {Object}
 */
export function serializeGQLObject (object:object) {
  return JSON.stringify(object)
    .replace(/"(\w+)":/ig, '$1:');
}

export default function gql (query:string, data?:Object | Array<any>):Promise<any> {
  if (data) {
    return new Promise((resolve) => {
      setTimeout(() => resolve(data), 300);
    });
  }

  const isMutation = query.trim().startsWith('mutation');
  const hasEmbeddedToken = !!getQueryParameter('access_token') || !!getQueryParameter('token');
  const isSkinnyProfilePath = typeof window !== 'undefined'
    && window.location.pathname.startsWith('/skinny-player-profile/');

  return fetch(GQL_URL, {
    method: 'POST',
    mode: 'cors',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': getAuthorizationHeader(),
    },
    body: JSON.stringify({ query: isMutation ? ` ${query} ` : `{ ${query} }` }),
  })
    .then(res => {
      const headers:string[] = [];
      res.headers.forEach(header => headers.push(header));

      const isTimeoutError = !!res.headers
        .get(TIMEOUT_RESPONSE_HEADER)
        ?.toLowerCase()
        ?.includes('timeout');

      if (res.ok) {
        return res.json();
      } else if (isTimeoutError) {
        return {
          data: {},
          errors: [{ message: GQL_ERROR.TIMEOUT }],
        };
      }

      throw new Error('Failed to get GQL response');
    })
    .then((data:any) => {
      const result = data.data;
      const errorMessage = get(data, 'errors[0].message', '');

      if (errorMessage === GQL_ERROR.UNAUTHORIZED && !hasEmbeddedToken && !isSkinnyProfilePath) {
        window.location.href = '/login';
      }

      return {
        ...result,
        errorMessage,
      };
    })
    .catch(console.error);
}
